import React, { useState } from 'react'
import { 
  LightBulbIcon,
  HomeIcon,
  CameraIcon,
  FireIcon,
  SunIcon,
  WifiIcon,
  BoltIcon,
  SignalIcon,
  PlayIcon,
  PauseIcon,
  CogIcon
} from '@heroicons/react/24/outline'
import { 
  LightBulbIcon as LightBulbSolid,
  HomeIcon as HomeSolid,
  CameraIcon as CameraSolid
} from '@heroicons/react/24/solid'

const deviceIcons = {
  light: { outline: LightBulbIcon, solid: LightBulbSolid },
  thermostat: { outline: FireIcon, solid: FireIcon },
  door_sensor: { outline: HomeIcon, solid: HomeSolid },
  camera: { outline: CameraIcon, solid: CameraSolid },
  humidifier: { outline: SunIcon, solid: SunIcon }
}

const statusColors = {
  online: 'text-green-500',
  offline: 'text-red-500',
  warning: 'text-yellow-500'
}

function DeviceCard({ device, onToggle, onUpdate }) {
  const [showControls, setShowControls] = useState(false)
  const [localValue, setLocalValue] = useState(device.brightness || device.value || 0)

  const IconComponent = device.value && (device.type === 'light' || device.type === 'camera') 
    ? deviceIcons[device.type]?.solid 
    : deviceIcons[device.type]?.outline || CogIcon

  const getStatusColor = () => {
    if (device.status === 'offline') return statusColors.offline
    if (device.battery && device.battery < 20) return statusColors.warning
    return statusColors.online
  }

  const formatLastUpdate = (timestamp) => {
    const now = new Date()
    const updateTime = new Date(timestamp)
    const diffInMinutes = Math.floor((now - updateTime) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Ahora'
    if (diffInMinutes < 60) return `${diffInMinutes}min`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`
    return `${Math.floor(diffInMinutes / 1440)}d`
  }

  const getDeviceValue = () => {
    switch (device.type) {
      case 'light':
        return device.value ? 'Encendida' : 'Apagada'
      case 'thermostat':
        return `${device.value}${device.unit}`
      case 'door_sensor':
        return device.value ? 'Abierta' : 'Cerrada'
      case 'camera':
        return device.value ? 'Grabando' : device.status === 'online' ? 'En línea' : 'Desconectada'
      case 'humidifier':
        return `${device.value}${device.unit}`
      default:
        return device.status
    }
  }

  const handleSliderChange = (newValue) => {
    setLocalValue(newValue)
  }

  const handleSliderCommit = () => {
    if (device.type === 'light') {
      onUpdate(device.id, 'setBrightness', localValue)
    } else {
      onUpdate(device.id, 'setValue', localValue)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <IconComponent 
            className={`h-8 w-8 ${device.value && (device.type === 'light' || device.type === 'camera') ? 'text-yellow-500' : 'text-gray-600'}`} 
          />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{device.name}</h3>
            <p className="text-sm text-gray-500">{device.room}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <WifiIcon className={`h-4 w-4 ${getStatusColor()}`} />
          {device.battery !== null && (
            <div className="flex items-center space-x-1">
              <BoltIcon className={`h-4 w-4 ${device.battery < 20 ? 'text-red-500' : 'text-gray-400'}`} />
              <span className="text-xs text-gray-500">{device.battery}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Status and Value */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-gray-900">
            {getDeviceValue()}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            device.status === 'online' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {device.status === 'online' ? 'En línea' : 'Desconectado'}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Última actualización: {formatLastUpdate(device.lastUpdate)}
        </p>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        {/* Toggle Button */}
        {device.actions.includes('toggle') && (
          <button
            onClick={() => onToggle(device.id)}
            disabled={device.status === 'offline'}
            className={`w-full py-2 px-4 rounded-md font-medium transition-colors duration-200 ${
              device.status === 'offline'
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : device.value
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {device.type === 'light' 
              ? (device.value ? 'Apagar' : 'Encender')
              : device.type === 'camera'
              ? (device.value ? 'Detener' : 'Iniciar')
              : 'Alternar'
            }
          </button>
        )}

        {/* Brightness/Value Slider */}
        {(device.actions.includes('setBrightness') || device.actions.includes('setHumidity')) && device.status === 'online' && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">
                {device.type === 'light' ? 'Brillo' : 'Nivel'}
              </label>
              <span className="text-sm text-gray-500">
                {device.type === 'light' ? device.brightness : device.value}
                {device.type === 'light' ? '%' : device.unit}
              </span>
            </div>
            <input
              type="range"
              min={device.type === 'light' ? 0 : 20}
              max={device.type === 'light' ? 100 : 80}
              value={device.type === 'light' ? device.brightness : device.value}
              onChange={(e) => handleSliderChange(parseInt(e.target.value))}
              onMouseUp={handleSliderCommit}
              onTouchEnd={handleSliderCommit}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        )}

        {/* Temperature Control */}
        {device.actions.includes('setTemperature') && device.status === 'online' && (
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => onUpdate(device.id, 'setTemperature', device.value - 0.5)}
              className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center font-bold"
            >
              -
            </button>
            <span className="text-xl font-semibold">{device.value}°C</span>
            <button
              onClick={() => onUpdate(device.id, 'setTemperature', device.value + 0.5)}
              className="w-10 h-10 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center font-bold"
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DeviceCard